import { AddOutlined, Public, Search } from '@mui/icons-material';
import { Autocomplete, Box, Button, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import { AgenciesGrid, AgencyDetails, AgencySetupWrapper, AppFormModal } from 'Application/components';
import { AgenciesGridData } from 'Application/data';
import { Agency, AgencySetupStep, RegionItem, RegionResponse, UserRole } from 'Application/models';
import { FC, useState, useEffect } from 'react';
import MainCard from 'ui-component/cards/MainCard';
import { useAgencyStore, useRegionStore } from 'Application/store';
import Loader from 'ui-component/Loader';
import { getUserRoleNameFromStorage } from 'Application/utils';

interface BritamAdminAgenciesProps {}

export const BritamAdminAgencies: FC<BritamAdminAgenciesProps> = () => {
    const { agencyData, getAgencies, isLoading } = useAgencyStore();
    const [isAddAgencyModalOpen, setIsAddAgencyModalOpen] = useState<boolean>(false);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [currentModalContentIndex, setCurrentModalContentIndex] = useState<number>(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const userRole = getUserRoleNameFromStorage();

    const { regions: countries, getRegions } = useRegionStore();

    const initialCountryValues: RegionResponse = [
        { name: 'Search by country', regionID: 0, flag: '', countryAbbreviation: '' },
        { name: 'All', regionID: 1, flag: '', countryAbbreviation: '' }
    ];
    const countryValues = countries ? [...initialCountryValues, ...countries] : initialCountryValues;

    // State for storing the region name.
    const [regionName, setRegionName] = useState<string | null>(null);

    // State for storing the region name, id and flag.
    const [region, setRegion] = useState<RegionItem>({ name: 'All', regionID: 1, flag: '', countryAbbreviation: '' });

    // const handleGoBack = () => {
    //     setCurrentModalContentIndex(currentModalContentIndex - 1);
    // };

    // const handleProceedToNextStep = () => {
    //     setCurrentModalContentIndex(currentModalContentIndex + 1);
    // };

    // const agencySetupSteps: AgencySetupStep[] = [
    //     {
    //         name: 'Details',
    //         component: <AgencyDetails proceedToNextStep={handleProceedToNextStep} />
    //     },
    //     {
    //         name: 'Documents',
    //         component: <AgencyDocuments proceedToNextStep={handleProceedToNextStep} goBack={handleGoBack} />
    //     },
    //     {
    //         name: 'Customization',
    //         component: <AgencyCustomization goBack={handleGoBack} proceedToNextStep={handleProceedToNextStep} />
    //     },
    //     {
    //         name: 'Setup confirmation',
    //         component: <AgencySetupConfirmation goBack={handleGoBack} />
    //     }
    // ];

    const handleCountryChange = (region: RegionItem) => {
        setRegion(region);
        region.name === 'All' ? setRegionName(null) : setRegionName(region.name);
        setCurrentPage(1);
    };

    const handleAddAgencyModalClose = () => {
        setIsAddAgencyModalOpen(false);
    };

    const handleAddAgencyModalOpen = () => {
        setCurrentModalContentIndex(0);
        setIsAddAgencyModalOpen(true);
    };

    const handleSearchChange = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    useEffect(() => {
        getAgencies(regionName, currentPage, rowsPerPage, searchTerm);
    }, [regionName, currentPage, rowsPerPage, searchTerm]);

    useEffect(() => {
        searchInput === '' && handleSearchChange();
    }, [searchInput]);

    useEffect(() => {
        getRegions();
    }, []);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <Box>
            <MainCard className="mb-4">
                <Box className="block sm:flex sm:justify-between sm:items-center">
                    <Typography className="text-[16px] font-medium">Intermediary List</Typography>
                    <Box
                        className={`flex justify-end items-center gap-4 mt-4 sm:mt-0 w-full ${userRole === UserRole.ROLE_ADMINISTRATOR ? 'sm:w-3/4' : 'sm:w-2/3'}`}
                    >
                        {userRole === UserRole.ROLE_ADMINISTRATOR && (
                            <Autocomplete
                                className="w-1/3"
                                size="small"
                                disableClearable
                                options={countryValues}
                                value={region}
                                onChange={(_, newValue) => newValue && handleCountryChange(newValue)}
                                getOptionLabel={(option) => option.name}
                                renderOption={(props, option) => (
                                    <MenuItem
                                        disabled={option.name === 'Search by country'}
                                        {...props}
                                        key={option.regionID}
                                        value={option.name}
                                        className={`flex gap-${option.name === 'All' ? 0 : 2} ${option.name === 'All' && '-ml-[1px]'}`}
                                    >
                                        {option.name !== 'Search by country' &&
                                            (option.name === 'All' ? (
                                                <InputAdornment position="start">
                                                    <Public sx={{ color: '#0369a1' }} />
                                                </InputAdornment>
                                            ) : (
                                                <img
                                                    src={`data:image/png;base64, ${option.flag}`}
                                                    alt={`Flag of ${option.name}`}
                                                    width={20}
                                                    height={20}
                                                />
                                            ))}
                                        <Typography>{option.name}</Typography>
                                    </MenuItem>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    {region.name === 'All' ? (
                                                        <Public className="ml-2 -mr-2" sx={{ color: '#0369a1' }} />
                                                    ) : (
                                                        <img
                                                            className="ml-2 -mr-2"
                                                            src={`data:image/png;base64, ${region?.flag}`}
                                                            alt={`Flag of ${region?.name}`}
                                                            width={20}
                                                            height={20}
                                                        />
                                                    )}
                                                </InputAdornment>
                                            )
                                        }}
                                        placeholder="Search by country"
                                    />
                                )}
                            />
                        )}
                        <TextField
                            className="w-1/3"
                            size="small"
                            id="searchValue"
                            label="Search by name or email"
                            variant="outlined"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearchChange()}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Search className="hover:cursor-pointer" onClick={handleSearchChange} />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Button
                            className={userRole === UserRole.ROLE_ADMINISTRATOR ? 'w-1/3' : ''}
                            disableElevation
                            onClick={handleAddAgencyModalOpen}
                            variant="contained"
                            startIcon={<AddOutlined />}
                        >
                            Create Intermediary
                        </Button>
                    </Box>
                </Box>
            </MainCard>
            {agencyData && (
                <AgenciesGrid
                    data={agencyData}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={setRowsPerPage}
                    setCurrentPage={setCurrentPage}
                />
            )}
            <AppFormModal
                children={<AgencyDetails setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage} />}
                modalIsOpen={isAddAgencyModalOpen}
                handleModalClose={handleAddAgencyModalClose}
            />
        </Box>
    );
};
